import { useCallback, useEffect, useState } from "react";
import { api, type City, type MenuProduct, type OrderType, type Place } from "./api";
import { useCart, type ChosenModifier } from "./state/cart";
import { CartSheet } from "./screens/CartSheet";
import { Checkout } from "./screens/Checkout";
import { Menu } from "./screens/Menu";
import { Order } from "./screens/Order";
import { Places } from "./screens/Places";
import { ProductSheet } from "./screens/ProductSheet";
import { money, Spinner } from "./ui";

/**
 * Витрина гостя: карта города, заведения и заказ.
 *
 * Маршрутизации в привычном смысле здесь нет: экранов четыре, и все они —
 * состояния одной страницы. В адресной строке живёт только токен заказа,
 * потому что с платёжной страницы гость возвращается именно по адресу.
 */

const CITY_KEY = "pf.city.v1";
const ORDER_KEY = "pf.order.v1";

type Screen = { name: "places" } | { name: "place"; pointId: string };

/**
 * Токен заказа из адреса `/order/<токен>`.
 *
 * С платёжной страницы гость возвращается именно сюда, и адрес — единственное,
 * на что здесь можно опереться: память браузера могла не пережить переход
 * через чужой домен.
 */
function orderTokenFromUrl(): string | null {
  const match = /^\/order\/([A-Za-z0-9_-]+)/.exec(window.location.pathname);
  return match?.[1] ?? null;
}

/**
 * Оболочка: выбирает город и ничего больше.
 *
 * Заказ живёт в отдельном компоненте, который монтируется, только когда город
 * уже известен. Так корзина не успевает завестись «ничьей»: она хранится по
 * городу, и хук, созданный до выбора, записал бы пустоту поверх настоящей
 * корзины гостя.
 */
export function App() {
  const [cities, setCities] = useState<City[] | null>(null);
  const [cityId, setCityId] = useState<string | null>(() => localStorage.getItem(CITY_KEY));
  const [fatal, setFatal] = useState<string | null>(null);

  const [returned] = useState(orderTokenFromUrl);

  useEffect(() => {
    if (!returned) return;

    localStorage.setItem(ORDER_KEY, returned);
    window.history.replaceState(null, "", "/app");
  }, [returned]);

  useEffect(() => {
    api
      .cities()
      .then(data => {
        setCities(data.cities);
        if (data.cities.length === 0) {
          setFatal("Платформа пока не работает ни в одном городе.");
        }
      })
      .catch((error: Error) => setFatal(error.message));
  }, []);

  /*
   * Заказ показывается, даже если города ещё нет.
   *
   * Гость вернулся с оплаты, а список городов мог не загрузиться на плохой
   * связи — и не показать ему оплаченный заказ было бы издевательством.
   */
  const savedOrder = localStorage.getItem(ORDER_KEY);
  if (fatal && savedOrder) {
    return (
      <div className="app">
        <Order
          token={savedOrder}
          onLeave={() => {
            localStorage.removeItem(ORDER_KEY);
            window.location.reload();
          }}
        />
      </div>
    );
  }

  if (fatal) {
    return (
      <div className="app">
        <div className="center">
          <h1>Пока пусто</h1>
          <p className="muted">{fatal}</p>
        </div>
      </div>
    );
  }

  if (!cities) {
    return (
      <div className="app">
        <Spinner text="Смотрим, где мы работаем" />
      </div>
    );
  }

  const city = cities.find(item => item.id === cityId) ?? cities[0]!;

  return (
    <CityApp
      key={city.id}
      city={city}
      cities={cities}
      onCity={next => {
        localStorage.setItem(CITY_KEY, next);
        setCityId(next);
      }}
    />
  );
}

function CityApp({
  city,
  cities,
  onCity
}: {
  city: City;
  cities: City[];
  onCity: (cityId: string) => void;
}) {
  const [screen, setScreen] = useState<Screen>({ name: "places" });
  const [place, setPlace] = useState<Place | null>(null);
  const [picked, setPicked] = useState<{
    product: MenuProduct;
    point: { id: string; name: string };
  } | null>(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  const [orderToken, setOrderToken] = useState<string | null>(
    () => localStorage.getItem(ORDER_KEY)
  );
  const [placing, setPlacing] = useState(false);
  const [orderError, setOrderError] = useState<string | null>(null);

  const cart = useCart(city.id);

  /*
   * Карточка заведения загружается вместе с меню.
   *
   * Из неё берутся адрес и доступные способы получения: оформление должно
   * предлагать только то, что здесь действительно можно.
   */
  useEffect(() => {
    if (screen.name !== "place") return;

    let cancelled = false;
    api
      .place(screen.pointId)
      .then(data => !cancelled && setPlace(data))
      .catch(() => !cancelled && setPlace(null));

    return () => {
      cancelled = true;
    };
  }, [screen]);

  const submit = useCallback(
    async (input: {
      type: OrderType;
      customerName: string;
      customerPhone: string;
      comment: string;
    }) => {
      const pointId = cart.items[0]?.pointId;
      if (!pointId) return;

      setPlacing(true);
      setOrderError(null);

      try {
        const order = await api.createOrder(
          {
            pointId,
            type: input.type,
            customerName: input.customerName,
            customerPhone: input.customerPhone,
            comment: input.comment,
            promoCode: cart.promoCode.trim() || undefined,
            items: cart.items.map(item => ({
              pointId: item.pointId,
              productId: item.productId,
              qty: item.qty,
              modifiers: item.modifiers.map(modifier => ({
                groupId: modifier.groupId,
                optionId: modifier.optionId,
                qty: modifier.qty
              }))
            }))
          },
          api.newKey()
        );

        /*
         * Токен сохраняется до перехода на оплату, а не после.
         *
         * С платёжной страницы гость возвращается на `/order/<токен>`, но
         * может и не вернуться — закрыть вкладку, потерять связь. Заказ,
         * записанный заранее, он найдёт, открыв витрину снова.
         */
        localStorage.setItem(ORDER_KEY, order.token);
        setOrderToken(order.token);
        cart.clear();
        setCheckoutOpen(false);
        setCartOpen(false);

        const payment = await api.startPayment(order.token, api.newKey());

        if (payment.confirmationUrl) {
          window.location.href = payment.confirmationUrl;
        }
      } catch (error) {
        setOrderError((error as Error).message);
      } finally {
        setPlacing(false);
      }
    },
    [cart]
  );

  const leaveOrder = useCallback(() => {
    localStorage.removeItem(ORDER_KEY);
    setOrderToken(null);
    setScreen({ name: "places" });
  }, []);

  if (orderToken) {
    return (
      <div className="app">
        <Top city={city} cities={cities} onCity={onCity} />
        <Order token={orderToken} onLeave={leaveOrder} />
      </div>
    );
  }

  return (
    <div className="app">
      <Top
        city={city}
        cities={cities}
        onCity={onCity}
        place={screen.name === "place" ? place : null}
        onBack={screen.name === "place" ? () => setScreen({ name: "places" }) : undefined}
      />

      {screen.name === "places" ? (
        <Places city={city} onOpenPlace={pointId => setScreen({ name: "place", pointId })} />
      ) : (
        <Menu
          pointId={screen.pointId}
          onPick={(product, point) => setPicked({ product, point })}
        />
      )}

      {cart.count > 0 && !cartOpen && !checkoutOpen && !picked ? (
        <div className="dock">
          <div className="dock-inner">
            <button type="button" className="btn" onClick={() => setCartOpen(true)}>
              <span className="dock-count">{cart.count}</span>
              <span>Корзина</span>
              <span>{cart.pricing ? "…" : money(cart.totals.total)}</span>
            </button>
          </div>
        </div>
      ) : null}

      {picked ? (
        <ProductSheet
          product={picked.product}
          point={picked.point}
          onClose={() => setPicked(null)}
          onAdd={(modifiers: ChosenModifier[], qty: number) => {
            cart.add(picked.product, picked.point, modifiers, qty);
            setPicked(null);
          }}
        />
      ) : null}

      {cartOpen ? (
        <CartSheet
          items={cart.items}
          totals={cart.totals}
          pricing={cart.pricing}
          problem={cart.problem}
          promoCode={cart.promoCode}
          promoError={cart.promoError}
          onPromoCode={cart.setPromoCode}
          onClose={() => setCartOpen(false)}
          onQty={cart.setQty}
          onCheckout={() => {
            setCartOpen(false);
            setCheckoutOpen(true);
          }}
        />
      ) : null}

      {checkoutOpen ? (
        <Checkout
          place={place}
          total={cart.totals.total}
          busy={placing}
          error={orderError}
          onClose={() => setCheckoutOpen(false)}
          onSubmit={submit}
        />
      ) : null}
    </div>
  );
}

function Top({
  city,
  cities,
  onCity,
  place,
  onBack
}: {
  city: City;
  cities: City[];
  onCity: (cityId: string) => void;
  place?: Place | null;
  onBack?: () => void;
}) {
  return (
    <header className="top">
      {onBack ? (
        <button type="button" className="back" onClick={onBack} aria-label="Назад">
          ←
        </button>
      ) : null}

      <div className="top-title">
        <div className="eyebrow">{place ? "Заведение" : "Город"}</div>
        <strong>{place ? place.name : city.name}</strong>
      </div>

      {/*
        Выбор города — только когда городов больше одного.

        На старте город один, и список из одного пункта рядом с названием
        этого же города выглядит как недоделка.
      */}
      {!place && cities.length > 1 ? (
        <select
          className="city-pick"
          value={city.id}
          onChange={event => onCity(event.target.value)}
          aria-label="Город"
        >
          {cities.map(item => (
            <option key={item.id} value={item.id}>
              {item.name}
            </option>
          ))}
        </select>
      ) : null}
    </header>
  );
}
